// Le premier fil d'un nouveau venu.
//
// ─────────────────────────────────────────────────────────────────────────────
// LE PROBLÈME
//
// Depuis la cinquième passe, Koinonia n'est pas une place publique : chacun ne
// voit QUE son cercle — lui-même, ses amis, et ceux à qui il s'est abonné.
// C'est ce qu'il fallait pour ouvrir la plateforme sans exposer la communauté.
//
// Mais cela crée un vide à l'entrée. Quelqu'un qui s'inscrit n'a ni ami ni
// abonnement : son fil est vide, et rien ne lui montre à quoi ressemble
// l'endroit. Un réseau qui accueille par une page blanche ne garde personne.
//
// ─────────────────────────────────────────────────────────────────────────────
// CE QUE FAIT CE FICHIER
//
// À la PREMIÈRE ouverture de session, et seulement si le membre n'a encore
// aucun ami, il est abonné aux « comptes d'accueil » déclarés dans
// `APP.decouverte` — aujourd'hui le compte du fondateur.
//
// ⚠️ POURQUOI LE FONDATEUR SUFFIT POUR AVOIR AUSSI LA PAGE KOINONIA.
// Une publication de page porte l'identifiant de celui qui l'a écrite
// (`authorId`) EN PLUS de celui de la page (`pageId`) — voir
// `ComposeurEspace.jsx`. Et le magasin force `visibilite: "tous"` sur toute
// publication de page ou de groupe, quel que soit le réglage de son auteur
// (`store.js`, `posts.add`). Les publications de KOINONIA arrivent donc dans
// le fil du nouveau venu par le même abonnement, sans requête de plus.
// Le jour où quelqu'un d'AUTRE écrira au nom de la page, il faudra ajouter son
// identifiant à la liste : c'est l'auteur qui compte, pas la page.
//
// ⚠️ ET CE QUI NE MARCHERA PAS TOUT SEUL. Les publications PERSONNELLES du
// fondateur ne suivent pas la même règle : elles prennent son réglage
// « Qui peut voir mes publications ». S'il est sur « Mes amis », un abonné
// n'en verra aucune — le serveur les refuse, et c'est bien ainsi. Pour que le
// nouveau venu voie autre chose que la page, le fondateur doit publier
// ouvert à tous.
//
// ─────────────────────────────────────────────────────────────────────────────
// CE QU'IL NE FAUT PAS FAIRE
//
// · Pas de rattrapage à chaque ouverture. Un membre qui se désabonne a pris
//   une décision ; la lui reprendre à la session suivante serait la nier. Le
//   passage est donc noté sur l'appareil, et ne se rejoue pas.
// · Pas d'abonnement forcé à qui a déjà des amis : celui-là a déjà un fil.
// · Pas de bruit en cas d'échec. Si le compte d'accueil n'autorise pas les
//   abonnés, le serveur refuse — et le nouveau venu n'a pas à lire une erreur
//   qui ne le concerne pas. On réessaiera simplement à la prochaine session.

import Store from "./store.js";
import { APP } from "./config.js";

const CLE = (uid) => `kn.accueil.${uid}`;

const dejaFait = (uid) => {
  try {
    return localStorage.getItem(CLE(uid)) === "1";
  } catch {
    return false;
  }
};

const noter = (uid) => {
  try {
    localStorage.setItem(CLE(uid), "1");
  } catch {
    /* navigation privée, stockage plein : tant pis, on réessaiera */
  }
};

/**
 * Abonne un nouveau venu aux comptes d'accueil.
 *
 * @param {object} moi        la fiche du membre qui vient d'ouvrir sa session
 * @param {string[]} relations les amitiés connues au moment de l'appel
 */
export async function accueillir(moi, relations) {
  const comptes = (APP.decouverte?.membres || []).filter((uid) => uid && uid !== moi?.uid);
  if (!moi?.uid || !comptes.length || dejaFait(moi.uid)) return;

  // Un membre qui a déjà des amis a déjà un fil : on ne s'invite pas.
  const aDesAmis = (relations || []).some((r) => r.etat === "acceptee");
  if (aDesAmis) return noter(moi.uid);

  let unSucces = false;
  for (const uid of comptes) {
    try {
      await Store.abonnes.abonner(uid);
      unSucces = true;
    } catch {
      /* refus du serveur, réseau coupé : silence, on retentera */
    }
  }
  // On ne note le passage QUE s'il a servi. Sinon les cinquante personnes
  // arrivées le soir où le réglage était encore éteint n'auraient jamais eu
  // de second essai.
  if (unSucces) noter(moi.uid);
}
